import * as React from "react";
import { useEffect, useState, useRef } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { localDate } from "../../utils/utils";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import { useTheme } from "@mui/material/styles";
import { apiRequest } from "../../utils/api";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import { Card, CardHeader, Divider, Grid, Menu, MenuItem } from "@mui/material";
import CardContent from "@mui/material/CardContent";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import Grow from "@mui/material/Grow";
import Pagination from "@mui/material/Pagination";
import { useCustomNavigate } from "../../hooks/useCustomNavigate";
import { useAlert } from "../../context/AlertContext";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CircularProgress from "@mui/material/CircularProgress";

type MindMapType = {
  uuid: string;
  title: string;
  nodes: { data?: { label?: string } }[];
  created_at: string;
  status: number;
};

interface MindMapLibraryPanelProps {
  refreshTrigger?: number;
}

const MindMapLibraryPanel: React.FC<MindMapLibraryPanelProps> = ({ refreshTrigger }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const itemBoxRef = React.useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(0);
  const [mindMapLists, setMindMapLists] = useState<MindMapType[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const itemHeight = 184;
  const uiLanguage = Cookies.get("ui_language") || "en";
  const navigate = useCustomNavigate();
  const { setAlert } = useAlert();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedUuid, setSelectedUuid] = React.useState<string | null>(null);
  const [deletingUuid, setDeletingUuid] = React.useState<string | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [prevMindMapLists, setPrevMindMapLists] = useState<MindMapType[]>([]);

  useEffect(() => {
    if (itemBoxRef.current) {
      const itemBoxHeight = itemBoxRef.current.clientHeight;
      const itemsPerPage = Math.floor(itemBoxHeight / (itemHeight + 16)) * 4;
      setItemsPerPage(itemsPerPage || 6);
    }
  }, [itemBoxRef]);

  const getMindMapLists = async () => {
    try {
      const res = await apiRequest(`/api/v1/mind-map/?page=${page}&page_size=${itemsPerPage}`, "GET");
      setMindMapLists(res.data.items);
      setTotalCount(res.data.total_count);
    } catch (error) {
      console.error("An unknown error occurred:", error);
    }
  };

  useEffect(() => {
    getMindMapLists();
  }, [refreshTrigger, page, itemsPerPage]);

  useEffect(() => {
    const isPollingNeeded = mindMapLists.some((map) => map.status === 3);

    if (isPollingNeeded && !pollingIntervalRef.current) {
      // ポーリング開始
      pollingIntervalRef.current = setInterval(() => {
        getMindMapLists();
      }, 5000); // 5秒ごと
    } else if (!isPollingNeeded && pollingIntervalRef.current) {
      // ポーリング停止
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    // クリーンアップ関数
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [mindMapLists]);

  useEffect(() => {
    // mindMapListsが更新された後に実行
    mindMapLists.forEach((currentMap) => {
      const prevMap = prevMindMapLists.find((pMap) => pMap.uuid === currentMap.uuid);
      // 以前のステータスが3で、現在のステータスが3でない場合に完了とみなす
      if (prevMap && prevMap.status === 3 && currentMap.status !== 3) {
        setAlert("success", t("マインドマップ「" + currentMap.title + "」の生成が完了しました。"));
      }
    });
    // 現在のリストを以前のリストとして保存
    setPrevMindMapLists(mindMapLists);
  }, [mindMapLists, setAlert, t]);

  const handleDelete = async (uuid: string) => {
    setDeletingUuid(uuid);
    try {
      await apiRequest(`/api/v1/mind-map/${uuid}`, "DELETE");
      setAlert("success", t("file.deleteFile.success"));
      getMindMapLists();
    } catch (error) {
      console.error("Error deleting item:", error);
      setAlert("error", t("file.deleteFile.error"));
    } finally {
      setDeletingUuid(null);
    }
  };

  const [dialogOpen, setDialogOpen] = React.useState(false);

  const handleDialogOpen = (uuid: string) => {
    setSelectedUuid(uuid);
    setDialogOpen(true);
    handleMenuClose();
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleConfirmedDelete = () => {
    if (selectedUuid) {
      handleDelete(selectedUuid);
    }
    handleDialogClose();
  };

  const handleNewFile = async () => {
    try {
      await apiRequest("/api/v1/mind-map/create", "POST", {});
      getMindMapLists().then(() => {
        setAlert("success", t("library.mindmap.message.create"));
      });
    } catch (error) {
      console.error("An unknown error occurred:", error);
    }
  };

  const firstLabel = (nodes: { data?: { label?: string } }[]) => {
    if (nodes && nodes[0] && nodes[0].data && nodes[0].data.label) {
      return nodes[0].data.label.trim();
    } else {
      return "";
    }
  };

  const hoverStyles = {
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      opacity: 0.7,
    },
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, uuid: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedUuid(uuid);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center" }} mb={1} px={2} pt={2} gap={1}>
        <AccountTreeOutlinedIcon color={"secondary"} />
        <Typography variant="h6" component={"h2"} sx={{ position: "relative" }}>
          {t("library.title")}
        </Typography>
        <Box flexGrow={1} />
        <Button
          disableElevation
          onClick={handleNewFile}
          variant={"text"}
          sx={{ display: "flex", alignItems: "center" }}
        >
          <AddCircleIcon sx={{ mr: 1 }} />
          <Typography variant="button">{t("library.mindmap.create")}</Typography>
        </Button>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", px: 2, width: "100%" }} ref={itemBoxRef}>
        <Grid container spacing={2} sx={{ height: "100%" }}>
          {mindMapLists.map((mindMap, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index} sx={{}}>
              <Grow in={true} key={mindMap.uuid} timeout={(index + 1) * 100 > 1500 ? 1500 : (index + 1) * 100}>
                <Box
                  onClick={() => {
                    if (mindMap.status !== 3) {
                      navigate("/mind-map/" + mindMap.uuid);
                    }
                  }}
                  sx={{
                    height: "100%",
                    textDecoration: "none",
                    cursor: mindMap.status === 3 ? "default" : "pointer",
                    borderRadius: 1,
                    overflow: "hidden",
                  }}
                >
                  <Card
                    elevation={0}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      ...hoverStyles,
                      backgroundColor: "background.paper",
                      borderTop: `4px solid transparent`,
                      borderImageSource: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                      borderImageSlice: 1,
                      minHeight: itemHeight,
                      height: "100%",
                      maxHeight: 250,
                    }}
                  >
                    <CardHeader
                      title={
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          {mindMap.status === 3 && <CircularProgress size={16} sx={{ mr: 1 }} />}
                          {mindMap.title}
                        </Box>
                      }
                      titleTypographyProps={{
                        variant: "subtitle2",
                        sx: {
                          whiteSpace: "normal",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          height: 40,
                        },
                      }}
                      action={
                        <IconButton
                          edge="end"
                          aria-label="more"
                          onClick={(event) => {
                            event.stopPropagation();
                            event.preventDefault();
                            handleMenuOpen(event, mindMap.uuid);
                          }}
                          sx={{ mr: 0 }}
                        >
                          <MoreVertIcon fontSize={"small"} />
                        </IconButton>
                      }
                    />
                    <Divider />
                    <CardContent sx={{ display: "flex", justifyContent: "space-between", py: 1 }}>
                      <Typography
                        variant="caption"
                        color="text.primary"
                        sx={{
                          whiteSpace: "normal",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {firstLabel(mindMap.nodes)}
                      </Typography>
                    </CardContent>
                    <Box flexGrow={1} />
                    <CardContent sx={{ display: "flex", justifyContent: "space-between", py: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {mindMap.nodes.length}{" "}
                        <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                          nodes
                        </Box>
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {localDate(mindMap.created_at, uiLanguage)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
              </Grow>
            </Grid>
          ))}
        </Grid>
      </Box>
      <Box sx={{ width: "100%", display: "flex", justifyContent: "center", my: 2 }}>
        <Pagination
          color={"primary"}
          count={Math.ceil(totalCount / itemsPerPage)}
          variant="outlined"
          page={page + 1}
          onChange={(event, value) => setPage(value - 1)}
        />
      </Box>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        {selectedUuid && (
          <MenuItem
            onClick={() => selectedUuid && handleDialogOpen(selectedUuid)}
            disabled={mindMapLists.find((map) => map.uuid === selectedUuid)?.status === 3}
          >
            {deletingUuid === selectedUuid ? (
              <CircularProgress size={24} />
            ) : (
              <DeleteIcon fontSize="small" sx={{ mr: 1 }} color="error" />
            )}
            {t("common.delete")}
          </MenuItem>
        )}
      </Menu>

      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>{t("file.deleteFile.title")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("file.deleteFile.description")}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button disableElevation onClick={handleDialogClose} color="primary">
            {t("common.cancel")}
          </Button>
          <Button onClick={handleConfirmedDelete} color="error" disabled={Boolean(deletingUuid)}>
            {deletingUuid ? <CircularProgress size={24} /> : t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MindMapLibraryPanel;
